export type ConsentChoice = 'accepted' | 'rejected';
import { validWebsiteConsent, WEBSITE_CONSENT_POLICY, type WebsiteConsentSnapshot } from '@workspace/content-migrations/meta-website-policy';
type Category = 'statistics' | 'marketing';
type Site = { hostname: string; protocol: string };
export const SHARED_CONSENT_COOKIE = 'bodyshape_consent_v3';
export const OPEN_CONSENT_PREFERENCES_EVENT = 'bodyshape:open-cookie-preferences';
export function openConsentPreferences(): void {
  window.dispatchEvent(new Event(OPEN_CONSENT_PREFERENCES_EVENT));
}
const keys = { statistics: 'bodyshape.analytics-consent.v1', marketing: 'bodyshape.marketing-consent.v2' };
const evidenceCookie = 'bodyshape_marketing_decision_v2';
const evidenceKey = 'bodyshape.marketing-decision.v2';
export const isSalonHost = (host: string) => host === 'bodyshapesalon.nl' || host.endsWith('.bodyshapesalon.nl');

function savedChoices(): [ConsentChoice | null, ConsentChoice | null] {
  const entries = document.cookie.split(';').map(v => v.trim()).filter(v => v.startsWith(`${SHARED_CONSENT_COOKIE}=`));
  if (entries.length !== 1) return [null, null];
  const value = entries[0].slice(SHARED_CONSENT_COOKIE.length + 1);
  if (!/^[ar-]\.[ar-]$/.test(value)) return [null, null];
  return value.split('.').map(v => v === 'a' ? 'accepted' : v === 'r' ? 'rejected' : null) as [ConsentChoice | null, ConsentChoice | null];
}

export function readSiteConsent(category: Category, site: Site = window.location): ConsentChoice | null {
  try {
    // Never revive an old host-local acceptance after a shared refusal, reset or expiry.
    // Existing visitors choose once again when moving to the shared-consent version.
    if (isSalonHost(site.hostname)) {
      if (site.protocol !== 'https:') return null;
      const current = savedChoices()[category === 'statistics' ? 0 : 1];
      if (current !== null || category === 'marketing') return current;
      // Preserve the independent statistics choice, never old marketing permission.
      // Any current cookie (including malformed/duplicate) takes precedence.
      const cookies = document.cookie.split(';').map(v => v.trim());
      if (cookies.some(v => v.startsWith(`${SHARED_CONSENT_COOKIE}=`))) return null;
      const legacy = cookies.filter(v => v.startsWith('bodyshape_consent_v2='));
      const value = legacy.length === 1 ? legacy[0].slice('bodyshape_consent_v2='.length) : '';
      return /^[ar-]\.[ar-]$/.test(value) ? value[0] === 'a' ? 'accepted' : value[0] === 'r' ? 'rejected' : null : null;
    }
    const value = window.localStorage.getItem(keys[category]);
    return value === 'accepted' || value === 'rejected' ? value : null;
  } catch {
    return null;
  }
}

export function writeSiteConsent(category: Category, choice: ConsentChoice | null, site: Site = window.location): void {
  if (isSalonHost(site.hostname)) {
    if (site.protocol !== 'https:') return;
    try {
      const choices = savedChoices();
      if (category === 'marketing' && choices[0] === null) choices[0] = readSiteConsent('statistics', site);
      choices[category === 'statistics' ? 0 : 1] = choice;
      const value = choices.map(v => v === 'accepted' ? 'a' : v === 'rejected' ? 'r' : '-').join('.');
      document.cookie = `${SHARED_CONSENT_COOKIE}=${value}; Domain=bodyshapesalon.nl; Path=/; Max-Age=15552000; SameSite=Lax; Secure`;
      if (category === 'marketing') document.cookie = `${evidenceCookie}=${choice === null ? '' : `${choice === 'accepted' ? 'a' : 'r'}.${Date.now()}`}; Domain=bodyshapesalon.nl; Path=/; Max-Age=${choice === null ? 0 : 15552000}; SameSite=Lax; Secure`;
    } catch {
      // If cookies are blocked, no optional tracker receives persisted permission.
    }
    return;
  }
  try {
    if (choice === null) window.localStorage.removeItem(keys[category]);
    else window.localStorage.setItem(keys[category], choice);
    if (category === 'marketing') {
      if (choice === null) window.localStorage.removeItem(evidenceKey);
      else window.localStorage.setItem(evidenceKey, `${choice === 'accepted' ? 'a' : 'r'}.${Date.now()}`);
    }
  } catch {
    // Preview/browser storage restrictions must not grant consent or crash the page.
  }
}

export function marketingConsentSnapshot(): WebsiteConsentSnapshot {
  let evidence = '';
  try {
    if (isSalonHost(window.location.hostname)) {
      const entries = document.cookie.split(';').map(v => v.trim()).filter(v => v.startsWith(`${evidenceCookie}=`));
      if (entries.length === 1) evidence = entries[0].slice(evidenceCookie.length + 1);
    } else evidence = window.localStorage.getItem(evidenceKey) ?? '';
  } catch { /* Consent remains absent. */ }
  const match = /^([ar])\.(\d{13})$/.exec(evidence);
  return validWebsiteConsent(match ? {
    marketing: match[1] === 'a' && readSiteConsent('marketing') === 'accepted',
    policy: WEBSITE_CONSENT_POLICY, decidedAt: Number(match[2]), source: 'cookie_preferences',
  } : null);
}
